'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import Image from 'next/image';

import VerifiedTick from '@/assets/icons/verified-tick.svg';
import LeftArrow from '@/assets/images/arrow-left.svg';
import UploadingLoadingIcon from '@/assets/images/uploading-loading-icon.svg';
import Button from '@/components/V2/Button';
import ButtonLink from '@/components/V2/ButtonLink';
import AuthToast from '@/components/V2/Popup/AuthToast';
import { AUTH_MODAL_TOAST_CONTAINER } from '@/layouts/ToastWrapper/containerIds';
import { useResendCodeMutation, useVerify2FAForgotPasswordMutation } from '@/store/api/twoFactorAuthentication';

interface EnterCodeProps {
  onVerify?: (recoveryCodes: string) => void;
  onBack?: () => void;
  inPage?: boolean;
  customer: { customerID: number; customerTypeID: number } | null;
  codeData?: { smsSendTo: string; emailSendTo: string; expiry: string };
}

type ValidationState = 'idle' | 'success' | 'error';

const EnterSecurityCode: React.FC<EnterCodeProps> = ({
  onVerify = () => {},
  onBack = () => {},
  inPage = false,
  customer,
}) => {
  const [values, setValues] = useState<string[]>(['', '', '', '', '', '']);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const [validationState, setValidationState] = useState<ValidationState>('idle');
  const [isLoading, setIsLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const [verifyCode, { isLoading: isVerifying }] = useVerify2FAForgotPasswordMutation();
  const [resendCode, { isLoading: isResending }] = useResendCodeMutation();

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [timeLeft]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleInputChange = useCallback(
    (index: number, value: string) => {
      const digitsOnly = value.replace(/\D/g, '');

      if (digitsOnly.length === 0) {
        const newValues = [...values];
        newValues[index] = '';
        setValues(newValues);
        return;
      }

      if (digitsOnly.length === 1) {
        const newValues = [...values];
        newValues[index] = digitsOnly;
        setValues(newValues);

        if (index < 5) {
          inputRefs.current[index + 1]?.focus();
        }
      } else {
        const newValues = [...values];
        const remainingSlots = 6 - index;
        const digitsToUse = digitsOnly.slice(0, remainingSlots);

        for (let i = 0; i < digitsToUse.length; i++) {
          if (index + i < 6) {
            newValues[index + i] = digitsToUse[i];
          }
        }

        setValues(newValues);

        const nextIndex = Math.min(index + digitsToUse.length, 5);
        setTimeout(() => {
          inputRefs.current[nextIndex]?.focus();
        }, 0);
      }
    },
    [values]
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent) => {
      if (e.key === 'Backspace' && !values[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    },
    [values]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData('text').replace(/\D/g, '');

      if (pastedData.length > 0) {
        const newValues = [...values];
        const pastedDigits = pastedData.slice(0, 6);

        const startIndex = focusedIndex !== null ? focusedIndex : 0;

        for (let i = 0; i < pastedDigits.length && startIndex + i < 6; i++) {
          newValues[startIndex + i] = pastedDigits[i];
        }

        setValues(newValues);

        const nextEmptyIndex = newValues.findIndex((val, idx) => idx > startIndex && !val);
        const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : Math.min(startIndex + pastedDigits.length, 5);

        setTimeout(() => {
          inputRefs.current[focusIndex]?.focus();
        }, 0);
      }
    },
    [values, focusedIndex]
  );

  const handleVerify = useCallback(async () => {
    if (!customer?.customerID) {
      toast(
        ({ closeToast }) => (
          <AuthToast type="error" message="Customer information not available" closeToast={closeToast} />
        ),
        { containerId: AUTH_MODAL_TOAST_CONTAINER }
      );
      return;
    }

    const code = values.join('');
    if (code.length !== 6) {
      toast(
        ({ closeToast }) => (
          <AuthToast type="error" message="Please enter the security code." closeToast={closeToast} />
        ),
        { containerId: AUTH_MODAL_TOAST_CONTAINER }
      );
      return;
    }

    setIsLoading(true);
    setValidationState('idle');

    try {
      const response = await verifyCode({
        customerId: customer.customerID,
        code,
        method: 1,
      }).unwrap();

      if (response.success) {
        setValidationState('success');
        setTimeout(() => onVerify(response.message), 1000);
      } else {
        setValidationState('error');
        toast(
          ({ closeToast }) => (
            <AuthToast type="error" message={response.message || 'Failed to verify code'} closeToast={closeToast} />
          ),
          { containerId: AUTH_MODAL_TOAST_CONTAINER }
        );
        setTimeout(() => setValidationState('idle'), 3000);
      }
    } catch (error: any) {
      setValidationState('error');
      const errorMessage =
        error?.data?.message || error?.data?.exceptionMessage || 'The code you entered is incorrect. Please try again.';
      toast(({ closeToast }) => <AuthToast type="error" message={errorMessage} closeToast={closeToast} />, {
        containerId: AUTH_MODAL_TOAST_CONTAINER,
      });
      setTimeout(() => setValidationState('idle'), 3000);
    } finally {
      setIsLoading(false);
    }
  }, [values, onVerify, customer, verifyCode]);

  const handleResend = useCallback(async () => {
    if (!customer?.customerID || !canResend) return;

    try {
      const response = await resendCode({
        customerId: customer.customerID,
        method: 1,
      }).unwrap();

      if (response.success && response.data) {
        setTimeLeft(60);
        setCanResend(false);
        setValues(['', '', '', '', '', '']);
        setValidationState('idle');

        toast(
          ({ closeToast }) => <AuthToast type="success" message="New code sent successfully" closeToast={closeToast} />,
          { containerId: AUTH_MODAL_TOAST_CONTAINER }
        );
      } else {
        toast(
          ({ closeToast }) => (
            <AuthToast type="error" message={response.message || 'Failed to resend code'} closeToast={closeToast} />
          ),
          { containerId: AUTH_MODAL_TOAST_CONTAINER }
        );
      }
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.data?.exceptionMessage || 'Failed to resend code';
      toast(({ closeToast }) => <AuthToast type="error" message={errorMessage} closeToast={closeToast} />, {
        containerId: AUTH_MODAL_TOAST_CONTAINER,
      });
    }
  }, [customer, canResend, resendCode]);

  const getBoxClasses = (index: number) => {
    const value = values[index];
    const isFocused = focusedIndex === index;
    const isEmpty = !value;

    const WHITE_BG = 'v2-bg-white';

    let borderClasses = '';
    let bgClasses = '';

    if (validationState === 'success') {
      borderClasses = 'v2-border-[#154734] v2-border-2';
      bgClasses = 'v2-bg-[#E2E8E6]';
    } else if (validationState === 'error') {
      borderClasses = 'v2-border-[#D92D20] v2-border-2';
      bgClasses = 'v2-bg-[#FEF3F2]';
    } else if (isFocused || !isEmpty) {
      borderClasses = 'v2-border-[#164634] v2-border-2';
      bgClasses = WHITE_BG;
    } else {
      borderClasses = 'v2-border-[#D0D5DD] v2-border';
      bgClasses = WHITE_BG;
    }

    return `v2-w-[45px] md:v2-w-[80px] v2-h-[48px] md:v2-h-[80px] v2-rounded-lg v2-p-2 v2-flex v2-items-center v2-justify-center ${borderClasses} ${bgClasses}`;
  };

  const getInputClasses = (index: number) => {
    const value = values[index];
    const isEmpty = !value;

    let textColorClasses = '';

    if (validationState === 'success') {
      textColorClasses = 'v2-text-[#154734]';
    } else if (validationState === 'error') {
      textColorClasses = 'v2-text-[#D92D20]';
    } else if (!isEmpty) {
      textColorClasses = 'v2-text-[#154734]';
    } else {
      textColorClasses = 'v2-text-[#D0D5DD]';
    }

    return `v2-w-[30px] md:v2-w-[64px] v2-h-8 md:v2-h-[64px] v2-text-center v2-border-0 v2-outline-none v2-bg-transparent v2-p-0 v2-font-sweet-sans-pro !v2-text-[24px] md:!v2-text-[48px] !v2-font-normal !v2-leading-[64px] !v2-tracking-[-0.96px] placeholder:v2-text-[#D0D5DD] ${textColorClasses}`;
  };

  const getButtonContent = () => {
    if (validationState === 'success') {
      return (
        <div className="v2-flex v2-items-center v2-gap-2">
          <span className="v2-font-sweet-sans-pro !v2-font-bold">Verified</span>
          <Image src={VerifiedTick} alt="" className="v2-w-5 v2-h-5" />
        </div>
      );
    }
    if (isLoading || isVerifying) return 'Verifying...';
    return 'Verify';
  };

  const getButtonClasses = () => {
    if (validationState === 'success') {
      return 'v2-bg-[#12B76A] hover:v2-bg-[#0F9A5C]';
    }
    if (validationState === 'error') {
      return 'v2-bg-[#F04438] hover:v2-bg-[#D92D20]';
    }
    return 'v2-bg-[#164634] hover:v2-bg-[#0F2A20]';
  };

  return (
    <div>
      <ButtonLink
        scheme={'primary'}
        variant={'outlined'}
        className={`!v2-border-0 !v2-p-0 !v2-rounded-0 v2-font-sweet-sans-pro-bold v2-font-bold !v2-text-lg md:!v2-text-lg v2-w-fit v2-flex v2-items-center !v2-leading-[28px] v2-gap-3 ${
          inPage ? 'v2-mb-[32px]' : 'v2-mb-8 md:v2-mb-[34px]'
        }`}
        onClick={onBack}
      >
        <Image src={LeftArrow} alt="" width={24} height={24} />
        Back
      </ButtonLink>

      <h2 className="v2-font-sweet-sans-pro-bold v2-font-bold v2-text-2xl md:v2-text-3xl v2-text-[#101828] v2-mb-2 md:v2-mb-4">
        Enter 6 Digit Code
      </h2>
      <p className="v2-font-sweet-sans-pro v2-font-normal v2-text-lg v2-text-[#101828] v2-mb-8">
        Enter your verification code sent to your (phone / email)
      </p>

      <div
        className={`v2-flex v2-items-center v2-gap-2 md:v2-gap-3 ${
          inPage ? 'v2-mb-6 md:v2-mb-[58px]' : 'v2-mb-6 md:v2-mb-8'
        }`}
      >
        {[0, 1, 2].map(index => (
          <div key={index} className={getBoxClasses(index)}>
            <input
              ref={el => (inputRefs.current[index] = el)}
              type="text"
              maxLength={1}
              value={values[index]}
              placeholder="0"
              className={getInputClasses(index)}
              onChange={e => handleInputChange(index, e.target.value)}
              onFocus={e => {
                setFocusedIndex(index);
                e.target.select();
              }}
              onBlur={() => setFocusedIndex(null)}
              onKeyDown={e => handleKeyDown(index, e)}
              onPaste={handlePaste}
            />
          </div>
        ))}
        <div className="v2-w-[26px] v2-hidden md:v2-flex v2-items-center v2-justify-center">
          <p className="!v2-text-[48px] !v2-font-normal v2-text-[#D0D5DD] v2-leading-none v2-font-sweet-sans-pro !v2-tracking-[-0.96px]">
            -
          </p>
        </div>
        {[3, 4, 5].map(index => (
          <div key={index} className={getBoxClasses(index)}>
            <input
              ref={el => (inputRefs.current[index] = el)}
              type="text"
              maxLength={1}
              value={values[index]}
              placeholder="0"
              className={getInputClasses(index)}
              onChange={e => handleInputChange(index, e.target.value)}
              onFocus={e => {
                setFocusedIndex(index);
                e.target.select();
              }}
              onBlur={() => setFocusedIndex(null)}
              onKeyDown={e => handleKeyDown(index, e)}
              onPaste={handlePaste}
            />
          </div>
        ))}
      </div>

      <Button
        scheme="primary"
        variant="filled"
        btnSize="lg"
        type="button"
        onClick={handleVerify}
        disabled={values.some(v => !v) || isLoading || isVerifying}
        className={`v2-w-full v2-h-[44px] v2-text-white v2-font-sweet-sans-pro-bold v2-text-base v2-rounded-lg v2-border-0 v2-mb-4 v2-flex v2-items-center v2-justify-center v2-gap-2 v2-transition-colors !v2-font-bold ${getButtonClasses()}`}
      >
        {isLoading && <Image src={UploadingLoadingIcon} alt="" className="v2-w-6 v2-h-6 v2-animate-spin" />}
        {getButtonContent()}
      </Button>

      <p className="v2-text-[#475467] v2-text-lg v2-font-sweet-sans-pro v2-font-normal v2-text-center v2-hidden md:v2-block">
        Didn&apos;t receive a code?{' '}
        <button
          onClick={handleResend}
          disabled={!canResend || isResending}
          className={`v2-font-sweet-sans-pro-bold v2-font-bold v2-underline v2-bg-transparent v2-border-0 v2-p-0 ${
            canResend && !isResending
              ? 'v2-text-[#164634] v2-cursor-pointer'
              : 'v2-text-[#D0D5DD] v2-cursor-not-allowed'
          }`}
        >
          {isResending ? 'Sending...' : 'Resend'}
        </button>{' '}
        {!canResend && `in ${formatTime(timeLeft)}`}
      </p>
      <p className="v2-text-[#344054] v2-text-lg v2-font-sweet-sans-pro v2-font-normal v2-text-center md:v2-hidden v2-block">
        Didn&apos;t receive a code?
      </p>
      <p className="v2-text-[#344054] v2-text-lg v2-font-sweet-sans-pro v2-font-normal v2-text-center md:v2-hidden v2-block">
        <button
          onClick={handleResend}
          disabled={!canResend || isResending}
          className={`v2-font-sweet-sans-pro-bold v2-font-bold v2-bg-transparent v2-border-0 v2-p-0 ${
            canResend && !isResending
              ? 'v2-text-[#475467] v2-cursor-pointer'
              : 'v2-text-[#D0D5DD] v2-cursor-not-allowed'
          }`}
        >
          {isResending ? 'Sending...' : 'Resend'}
        </button>{' '}
        {!canResend && `in ${formatTime(timeLeft)}`}
      </p>
    </div>
  );
};

export default EnterSecurityCode;
